import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useCuentasBancariasAll } from "api/CuentaBancaria.hook"
import { FacturaReadingSimpleDtoProps } from "api/DTOs/FacturaReadingSimpleDto"
import { FlujoTesoreriaReadingDtoProps } from "api/DTOs/FlujoTesoreriaReadingDto"
import { ESTADO_COBRO } from "api/Types/EstadoCobro"
import { ESTADOS_PAGO } from "api/Types/EstadosPago"
import { TIPO_FLUJO_TESORERIA } from "api/Types/TipoFlujoTesoreria"
import { formatCurrency } from "common/utils/currency"
import { ImporteBodyTemplate } from "features/Documentos/templates"
import { nanoid } from "nanoid"
import { Skeleton } from "primereact/skeleton"
import { useTranslation } from "react-i18next"
import { frontendDateFormat } from "utils/formatDate"

interface ModalVerPagoCobrosProps {
  data?: FlujoTesoreriaReadingDtoProps[]
  setShowAddPagoModal: $TSMigrationFixMe
  loading: $TSMigrationFixMe
  importePendiente: $TSMigrationFixMe
  documentData: FacturaReadingSimpleDtoProps
}
const ModalVerPagosYCobros = ({
  data,
  setShowAddPagoModal,
  loading,
  importePendiente,
  documentData,
}: ModalVerPagoCobrosProps) => {
  const { t } = useTranslation()

  const { cuentaBancariaAll, cuentaBancariaAllIsLoading } =
    useCuentasBancariasAll({})
  const isCobro = Boolean(
    documentData?.tipoFlujoTesoreria === TIPO_FLUJO_TESORERIA.COBRO
  )

  const isCobradaPagadaCompleta = isCobro
    ? documentData?.estadoCobro?.estadoCobro === ESTADO_COBRO.COBRADO
    : documentData?.estadoPago?.estadoPago === ESTADOS_PAGO.PAGADA

  const infoPagoCobro = isCobro
    ? documentData?.estadoCobro
    : documentData?.estadoPago

  return (
    <div
      className={`${
        documentData?.tipoFlujoTesoreria
          ? "tw-flex tw-flex-col tw-w-full tw-border-t tw-border-neutral-50"
          : "tw-hidden"
      }`}
    >
      <div className='tw-flex tw-flex-row tw-justify-between tw-items-center tw-font-medium'>
        <h4>{t("crear.presupuesto.modal.paymentsCollectionsLabel")}</h4>
        <p className='tw-flex tw-flex-row tw-gap-3'>
          <span
            className={
              isCobradaPagadaCompleta
                ? "tw-text-success"
                : "tw-text-alternative"
            }
          >
            {isCobro
              ? documentData?.estadoCobro?.estadoCobro === ESTADO_COBRO.COBRADO
                ? `${t("cartera.status.collected")}`
                : `${t("cartera.status.parcialCollectedModal")}`
              : documentData?.estadoPago?.estadoPago === ESTADOS_PAGO.PAGADA
              ? `${t("cartera.status.paid")}`
              : `${t("cartera.status.parcialPaidModal")}`}
          </span>

          {!isCobradaPagadaCompleta && (
            <ImporteBodyTemplate
              importe={documentData?.importePendiente}
              divisa={documentData?.divisa ?? "€"}
            />
          )}
        </p>
      </div>

      {!loading && data?.length ? (
        <ul className='tw-flex tw-flex-col !tw-max-h-48 tw-my-4 tw-overflow-y-auto tw-overscroll-y-none	'>
          {data?.map((item: FlujoTesoreriaReadingDtoProps) => {
            return (
              <li
                key={nanoid()}
                className='tw-flex tw-flex-row tw-justify-between tw-text-sm tw-my-1 tw-mr-1'
              >
                <div className='tw-flex tw-flex-row tw-w-4/5'>
                  <span className={`${item.fecha ? "tw-mr-2" : ""}`}>
                    {item.fecha ? frontendDateFormat(new Date(item.fecha)) : ""}
                  </span>
                  {cuentaBancariaAllIsLoading ? (
                    <Skeleton className='tw-w-20 tw-h-full' />
                  ) : (
                    <span className='tw-max-w-full tw-text-ellipsis tw-text-left'>
                      {cuentaBancariaAll?.items?.find(
                        (account) => account.id === item?.cuentaBancariaId
                      )?.iban || `${t("cartera.status.sinCuentaBancaria")}`}
                    </span>
                  )}
                </div>
                <span
                  className={`tw-flex tw-justify-end tw-w-1/5 tw-font-medium ${
                    item.tipo === TIPO_FLUJO_TESORERIA.COBRO
                      ? "tw-text-success"
                      : "tw-text-danger-20"
                  }`}
                >
                  {`${
                    item.tipo === TIPO_FLUJO_TESORERIA.COBRO ? "" : "-"
                  } ${formatCurrency(item.importe)}`}
                </span>
              </li>
            )
          })}
        </ul>
      ) : null}

      {Boolean(infoPagoCobro?.motivo) && (
        <div className='tw-bg-neutral-10 tw-border tw-border-neutral-50 tw-rounded tw-flex tw-flex-col tw-w-full tw-h-fit tw-py-3 tw-px-4 tw-mb-3 tw-gap-2'>
          <span className='tw-text-sm tw-font-medium'>
            {isCobro
              ? t("modalVerDocumento.pagoCobro.invoiceColled")
              : t("modalVerDocumento.pagoCobro.invoicePaid")}
          </span>
          <div className='tw-flex tw-flex-col tw-text-sm tw-gap-2'>
            {infoPagoCobro?.fecha && (
              <div className='tw-flex tw-flex-row tw-gap-1 tw-items-center'>
                <div className='tw-w-4 tw-flex tw-justify-center'>
                  <FontAwesomeIcon
                    icon={["fal", "calendar-days"]}
                    className='tw-font-bold'
                  />
                </div>
                <span className='tw-font-medium'>
                  {t("modalVerDocumento.pagoCobro.date")}
                </span>
                <span>
                  {frontendDateFormat(new Date(infoPagoCobro?.fecha))}
                </span>
              </div>
            )}
            {infoPagoCobro?.usuarioEmail && (
              <div className='tw-flex tw-flex-row tw-gap-1 tw-items-center'>
                <div className='tw-w-4 tw-flex tw-justify-center'>
                  <FontAwesomeIcon
                    icon={["fal", "user"]}
                    className='tw-font-bold'
                  />
                </div>
                <span className='tw-font-medium'>
                  {t("modalVerDocumento.pagoCobro.mark")}
                </span>
                <span>{infoPagoCobro?.usuarioEmail}</span>
              </div>
            )}
            {infoPagoCobro?.motivo && (
              <div className='tw-flex tw-flex-row tw-gap-1 tw-flex-wrap tw-items-center'>
                <div className='tw-w-4 tw-flex tw-justify-center'>
                  <FontAwesomeIcon
                    icon={["fal", "note"]}
                    className='tw-font-bold'
                  />
                </div>
                <span className='tw-font-medium'>
                  {t("modalVerDocumento.pagoCobro.note")}
                </span>
                <span>{infoPagoCobro?.motivo}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {!isCobradaPagadaCompleta && (
        <span
          className={`tw-flex tw-justify-center tw-items-center tw-h-12 tw-rounded tw-bg-neutral-10 hover:!tw-bg-neutral-25 ${
            importePendiente > 0 ? "tw-cursor-pointer" : "tw-hidden"
          }`}
          onClick={() =>
            setShowAddPagoModal({
              data: documentData,
              show: true,
            })
          }
        >
          <span>{t("crear.presupuesto.modal.addLabel")}</span>
        </span>
      )}
    </div>
  )
}

export default ModalVerPagosYCobros
